import React, { Suspense } from 'react';

import { Route, Routes } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';

const Login = React.lazy(() => import('../pages/login/Login'));
const UserDashboard = React.lazy(() => import('../pages/dashboard/user-dashboard/UserDashboard'));
const ManagerDashboard = React.lazy(() => import('../pages/dashboard/manager-dashboard/ManagerDashboard'));
const CreateUser = React.lazy(() => import('../pages/create-user/CreateUser'));
const ManageUsers = React.lazy(() => import('../pages/manage-users/ManageUsers'));
const Course = React.lazy(() => import('../pages/course/Course'));
const CreateCourse = React.lazy(() => import('../pages/create-course/CreateCourse'));
const CreateQuestionnaire = React.lazy(() => import('../pages/create-questionnaire/CreateQuestionnaire'));
const ManageCourses = React.lazy(() => import('../pages/edit-courses/ManageCourses'));
const EditCoursePage = React.lazy(() => import('../pages/edit-courses/EditCoursePage'));
const ManagerLearningPlans = React.lazy(() =>
    import('../pages/learning-plans/manager-learning-plans/ManagerLearningPlans'),
);
const CreateLearningPlan = React.lazy(() => import('../pages/create-learning-plan/CreateLearningPlan'));
const EditLearningPlan = React.lazy(() => import('../pages/edit-learning-plan/EditLearningPlan'));
const UserLearningPlans = React.lazy(() => import('../pages/learning-plans/user-learning-plans/UserLearningPlans'));
const LearningPlan = React.lazy(() => import('../pages/learning-plans/user-learning-plans/LearningPlan'));
const Questionnaire = React.lazy(() => import('../pages/questionnaire/Questionnaire'));
const EditQuestionnairePage = React.lazy(() => import('../pages/edit-questionnaires/EditQuestionnairePage'));
const ManageQuestionnaires = React.lazy(() => import('../pages/edit-questionnaires/ManageQuestionnaires'));
const CourseStatistics = React.lazy(() => import('../pages/course-statistics/CourseStatistics'));
const UserStatistics = React.lazy(() => import('../pages/user-statistics/UserStatistics'));
const ResetPassword = React.lazy(() => import('../pages/reset-password/ResetPassword'));

import NotFound from '../pages/NotFound';
import CommonRoute from './CommonRoute';
import PageLoader from '../components/common/PageLoader';
import { useGlobal } from '../state';
import AuthenticatedLayout from '../layouts/AuthenticatedLayout';

export const RouterProvider = ({ user }) => {
    const [{ user: userInfo }] = useGlobal();
    const As = !!user ? AuthenticatedLayout : 'div';

    return (
        <As>
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route
                        path="/"
                        element={
                            <CommonRoute isAuthenticated={!!user}>
                                <Login />
                            </CommonRoute>
                        }
                    />

                    <Route
                        path="/reset-password/:linkId"
                        element={
                            <CommonRoute isAuthenticated={false}>
                                <ResetPassword />
                            </CommonRoute>
                        }
                    />

                    <Route
                        path="/dashboard"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={
                                    userInfo?.role == 'user' || userInfo?.role == 'manager' || userInfo?.role == 'admin'
                                }
                            >
                                {userInfo?.role == 'user' && <UserDashboard />}
                                {(userInfo?.role == 'manager' || userInfo?.role == 'admin') && <ManagerDashboard />}
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/manager-courses"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'manager'}>
                                <UserDashboard />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/create-user"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={userInfo?.role == 'manager' || userInfo?.role == 'admin'}
                            >
                                <CreateUser />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/create-course"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <CreateCourse />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/courses"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <ManageCourses />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/courses/:courseId"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <EditCoursePage />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/create-questionnaire"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <CreateQuestionnaire />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/questionnaires"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <ManageQuestionnaires />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/questionnaires/:questionnaireId"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <EditQuestionnairePage />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/users"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={userInfo?.role == 'manager' || userInfo?.role == 'admin'}
                            >
                                <ManageUsers />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/users/:userId"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={userInfo?.role == 'manager' || userInfo?.role == 'admin'}
                            >
                                <CreateUser />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/user-statistics/:userId"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={userInfo?.role == 'manager' || userInfo?.role == 'admin'}
                            >
                                <UserStatistics />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/learning-plans"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={
                                    userInfo?.role == 'user' || userInfo?.role == 'manager' || userInfo?.role == 'admin'
                                }
                            >
                                {(userInfo?.role == 'user' || userInfo?.role == 'manager') && <UserLearningPlans />}
                                {userInfo?.role == 'admin' && <ManagerLearningPlans />}
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/learning-plans/:planId"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={
                                    userInfo?.role == 'user' || userInfo?.role == 'manager' || userInfo?.role == 'admin'
                                }
                            >
                                <LearningPlan />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/create-learning-plan"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <CreateLearningPlan />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/learning-plan/:planId"
                        element={
                            <PrivateRoute isAuthenticated={!!user} permission={userInfo?.role == 'admin'}>
                                <EditLearningPlan />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/course"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={
                                    userInfo?.role == 'user' || userInfo?.role == 'manager' || userInfo?.role == 'admin'
                                }
                            >
                                <Course />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/course-statistics/:courseId"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={userInfo?.role == 'admin' || userInfo?.role == 'manager'}
                            >
                                <CourseStatistics />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/questionnaire/:questionnaireId"
                        element={
                            <PrivateRoute
                                isAuthenticated={!!user}
                                permission={
                                    userInfo?.role == 'user' || userInfo?.role == 'manager' || userInfo?.role == 'admin'
                                }
                            >
                                <Questionnaire />
                            </PrivateRoute>
                        }
                    />

                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </As>
    );
};

export default RouterProvider;
